import { z } from "zod";
import type { Condition, Value, Values } from "./schema";

export type Calculation =
  | { op: "literal"; value: string | number | boolean | null }
  | { op: "field"; fieldId: string }
  | { op: "concat"; parts: Calculation[] }
  | {
      op: "replace";
      input: Calculation;
      replacements: { find: string; replacement: string }[];
    }
  | { op: "if"; when: Condition; then: Calculation; else: Calculation };

export class CalculationError extends Error {}
const MAX_TEXT = 32768;
function boundedTree(input: unknown) {
  const pending: [unknown, number][] = [[input, 0]];
  let nodes = 0,
    characters = 0;
  while (pending.length) {
    const [value, depth] = pending.pop()!;
    if (++nodes > 2048 || depth > 32) return false;
    if (typeof value === "string") characters += value.length;
    if (characters > 65536) return false;
    if (value && typeof value === "object")
      for (const child of Object.values(value))
        pending.push([child, depth + 1]);
  }
  return true;
}
/** Strict data operations only: no code, expressions, network or filesystem access. */
export function createCalculationSchema(
  conditions: z.ZodType<Condition>,
): z.ZodType<Calculation> {
  const expression: z.ZodType<Calculation> = z.lazy(() =>
    z.discriminatedUnion("op", [
      z
        .object({
          op: z.literal("literal"),
          value: z.union([
            z.string().max(MAX_TEXT),
            z.number().finite(),
            z.boolean(),
            z.null(),
          ]),
        })
        .strict(),
      z
        .object({ op: z.literal("field"), fieldId: z.string().min(1).max(200) })
        .strict(),
      z
        .object({
          op: z.literal("concat"),
          parts: z.array(expression).min(1).max(64),
        })
        .strict(),
      z
        .object({
          op: z.literal("replace"),
          input: expression,
          replacements: z
            .array(
              z
                .object({
                  find: z.string().min(1).max(256),
                  replacement: z.string().max(1024),
                })
                .strict(),
            )
            .min(1)
            .max(64),
        })
        .strict(),
      z
        .object({
          op: z.literal("if"),
          when: conditions,
          then: expression,
          else: expression,
        })
        .strict(),
    ]),
  );
  return z
    .unknown()
    .superRefine((value, ctx) => {
      if (!boundedTree(value))
        ctx.addIssue({
          code: "custom",
          message: "Calculation exceeds its size or nesting limit",
        });
    })
    .pipe(expression)
    .superRefine((value, ctx) => {
      const pending: [Calculation, number][] = [[value, 0]];
      let count = 0;
      while (pending.length) {
        const [next, depth] = pending.pop()!;
        if (++count > 256 || depth > 16) {
          ctx.addIssue({
            code: "custom",
            message: "Calculation exceeds its operation or depth limit",
          });
          return;
        }
        if (next.op === "concat")
          for (const part of next.parts) pending.push([part, depth + 1]);
        else if (next.op === "replace") pending.push([next.input, depth + 1]);
        else if (next.op === "if")
          pending.push([next.then, depth + 1], [next.else, depth + 1]);
      }
    });
}

export function calculationConditions(expression: Calculation): Condition[] {
  if (!boundedTree(expression))
    throw new CalculationError("Calculation exceeds its size or nesting limit");
  const conditions: Condition[] = [],
    pending = [expression];
  while (pending.length) {
    const next = pending.pop()!;
    if (next.op === "if") {
      conditions.push(next.when);
      pending.push(next.then, next.else);
    } else if (next.op === "concat") pending.push(...next.parts);
    else if (next.op === "replace") pending.push(next.input);
  }
  return conditions;
}
export function calculationDependencies(expression: Calculation): string[] {
  if (!boundedTree(expression))
    throw new CalculationError("Calculation exceeds its size or nesting limit");
  const references = new Set<string>(),
    pending = [expression];
  const conditionRefs = (condition: Condition) => {
    if (condition.field) references.add(condition.field);
    for (const child of condition.all || condition.any || [])
      conditionRefs(child);
  };
  while (pending.length) {
    const next = pending.pop()!;
    if (next.op === "field") references.add(next.fieldId);
    else if (next.op === "concat") pending.push(...next.parts);
    else if (next.op === "replace") pending.push(next.input);
    else if (next.op === "if") {
      conditionRefs(next.when);
      pending.push(next.then, next.else);
    }
  }
  return [...references];
}

export function evaluateCalculation(
  expression: Calculation,
  values: Values,
  matches: (condition: Condition, values: Values) => boolean,
  text: (value: Value) => string = (value) =>
    value == null ? "" : String(value),
): Value {
  let steps = 0;
  const bound = (value: Value) => {
    if (typeof value === "string" && value.length > MAX_TEXT)
      throw new CalculationError("Calculated text exceeds 32,768 characters");
    return value;
  };
  const append = (left: string, right: string) => {
    if (left.length + right.length > MAX_TEXT)
      throw new CalculationError("Calculated text exceeds 32,768 characters");
    return left + right;
  };
  const evaluate = (node: Calculation, depth = 0): Value => {
    if (++steps > 256 || depth > 16)
      throw new CalculationError("Calculation exceeds its evaluation limit");
    if (node.op === "literal") return bound(node.value);
    if (node.op === "field")
      return bound(
        Object.hasOwn(values, node.fieldId) ? values[node.fieldId] : null,
      );
    if (node.op === "if")
      return evaluate(
        matches(node.when, values) ? node.then : node.else,
        depth + 1,
      );
    if (node.op === "concat") {
      let output = "";
      for (const part of node.parts)
        output = append(output, text(evaluate(part, depth + 1)));
      return output;
    }
    let output = String(bound(text(evaluate(node.input, depth + 1))));
    for (const replacement of node.replacements) {
      if (!replacement.find)
        throw new CalculationError("Replacement search text cannot be empty");
      let next = "",
        position = 0,
        found: number;
      while ((found = output.indexOf(replacement.find, position)) !== -1) {
        next = append(next, output.slice(position, found));
        next = append(next, replacement.replacement);
        position = found + replacement.find.length;
      }
      output = append(next, output.slice(position));
    }
    return output;
  };
  return evaluate(expression);
}
